import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table, Row, Col, Form, Button, Alert, Modal, Pagination, Dropdown, Card, Spinner, InputGroup, Badge } from 'react-bootstrap';
import { getMovimentiMagazzino, deleteMovimentoMagazzino, deleteMovimentiMagazzino, createMovimentoMagazzino, getDatiAzienda } from '../services/api';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'react-responsive';
import MovimentiMagazzinoPrint from '../common/MovimentiMagazzinoPrint';
import MobileListLayout from '../common/MobileListLayout';
import StandardPageHeader from '../common/StandardPageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faPlus, faEdit, faTrash, faPrint, faSort, faColumns, faCog, faUndo, faSearch, faTimes, faSortUp, faSortDown, faWarehouse, faBoxes, faEuroSign, faChartLine } from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortController from '../common/SortController';
import AdvancedFilter from '../common/AdvancedFilter';
import ColumnOrderController from '../common/ColumnOrderController';

const columnHelper = createColumnHelper();

// Passato come location.state ai link verso schede prodotto/ordine/cliente, così il loro
// pulsante "Torna alla lista" riporta qui invece che alla lista di default.
const BACK_TO_MOVIMENTI = { from: '/movimenti-magazzino' };

const getProdottoPath = (tipoProdotto, prodottoId) => {
    if (!prodottoId) return null;
    switch (tipoProdotto) {
        case 'Film': return `/film/${prodottoId}`;
        case 'Musica': return `/musica/${prodottoId}`;
        case 'Servizio': return `/servizi/${prodottoId}`;
        case 'Collectible': return `/collectibles/edit/${prodottoId}`;
        default: return null;
    }
};

const allColumnsMap = {
    id: "ID",
    prodotto_id: "Prodotto ID",
    tipo_prodotto: "Tipo Prodotto",
    titolo_prodotto: "Titolo Prodotto",
    nome_cliente: "Cliente",
    tipo_movimento: "Tipo Movimento",
    quantita: "Quantità",
    data_movimento: "Data Movimento",
    descrizione: "Descrizione",
    quantita_prima: "Quantità Prima",
    quantita_dopo: "Quantità Dopo",
    riferimento_ordine_id: "Ordine ID",
    riferimento_dettaglio_ordine_id: "Dettaglio Ordine ID",
};

const initialVisibleColumns = Object.keys(allColumnsMap);

const visibilityKey = 'table-visibility-movimenti';
const orderKey = 'table-column-order-movimenti';

const MovimentiMagazzinoList = () => {
    const isMobile = useMediaQuery({ maxWidth: 991 });
    const [movimenti, setMovimenti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [showRectifyModal, setShowRectifyModal] = useState(false);
    const [forceRefresh, setForceRefresh] = useState(0);
    const [newMovimentoData, setNewMovimentoData] = useState({
        prodotto_id: '',
        tipo_prodotto: 'Film',
        tipo_movimento: 'Carico',
        quantita: '',
        descrizione: ''
    });
    const [isPrinting, setIsPrinting] = useState(false);
    const [datiAzienda, setDatiAzienda] = useState(null);
    const [distinctValues, setDistinctValues] = useState({
        tipo_prodotto: [],
        tipo_movimento: [],
    });

    // Stati per selezione e eliminazione movimenti
    const [selectedMovimenti, setSelectedMovimenti] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const saved = localStorage.getItem(visibilityKey);
        if (saved) return JSON.parse(saved);
        return Object.keys(allColumnsMap).reduce((acc, key) => {
            acc[key] = initialVisibleColumns.includes(key);
            return acc;
        }, {});
    });

    const [columnOrder, setColumnOrder] = useState(() => {
        const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
        const saved = localStorage.getItem(orderKey);
        if (!saved) return defaultOrder;
        try {
            const parsedOrder = JSON.parse(saved).filter(c => c !== 'select' && allColumnsMap[c]);
            const mancanti = Object.keys(allColumnsMap).filter(c => !parsedOrder.includes(c));
            return ['select', ...parsedOrder, ...mancanti];
        } catch {
            return defaultOrder;
        }
    });

    const [sorting, setSorting] = useState(() => {
        const saved = sessionStorage.getItem('list-sorting-movimenti');
        return saved ? JSON.parse(saved) : [];
    });

    const [columnFilters, setColumnFilters] = useState(() => {
        const saved = sessionStorage.getItem('list-filters-movimenti');
        return saved ? JSON.parse(saved) : [];
    });
    
    // Debounced version of columnFilters for API calls
    const [debouncedColumnFilters, setDebouncedColumnFilters] = useState(columnFilters);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [isCondensed, setIsCondensed] = useState(() => {
        const saved = localStorage.getItem('table-condensed-movimenti');
        return saved ? JSON.parse(saved) : false;
    });

    const [columnSizing, setColumnSizing] = useState(() => {
        const saved = localStorage.getItem('table-column-sizing-movimenti');
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        localStorage.setItem(orderKey, JSON.stringify(columnOrder));
    }, [columnOrder]);

    useEffect(() => {
        sessionStorage.setItem('list-sorting-movimenti', JSON.stringify(sorting));
    }, [sorting]);

    useEffect(() => {
        sessionStorage.setItem('list-filters-movimenti', JSON.stringify(columnFilters));
    }, [columnFilters]);
    
    // Debounce columnFilters for API calls
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedColumnFilters(columnFilters);
        }, 1200); // 1200ms delay for filter changes

        return () => {
            clearTimeout(handler);
        };
    }, [columnFilters]);
    
    useEffect(() => {
        localStorage.setItem('table-condensed-movimenti', JSON.stringify(isCondensed));
    }, [isCondensed]);

    useEffect(() => {
        localStorage.setItem('table-column-sizing-movimenti', JSON.stringify(columnSizing));
    }, [columnSizing]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [totalMovimenti, setTotalMovimenti] = useState(0);

    const [refreshList, setRefreshList] = useState(0);

    const fetchMovimenti = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = debouncedColumnFilters.reduce((acc, filter) => {
                  const { id, value } = filter;
                  if (Array.isArray(value) && value.length > 0) acc[id] = { 'in': value };
                  else if (typeof value === 'string' && value.toUpperCase() === 'N/A') acc[id] = null;
                  else if (value !== undefined && value !== '') acc[id] = value;
                  return acc;
                }, {});
            
            // Applica la ricerca globale se presente
            if (searchTerm) {
                filters._global_search = searchTerm;
            }

            const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);if (sortParams.length === 0) {
                sortParams.push('id:desc'); // Default sort
            }
            const response = await getMovimentiMagazzino((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, JSON.stringify(filters));
            
            setMovimenti(response.data.movimenti);
            setTotalMovimenti(response.data.total);
            if (response.data.facets) {
                setDistinctValues(response.data.facets);
            }
        } catch (err) {
            setError("Errore nel caricamento dei movimenti di magazzino.");
            console.error("Errore fetchMovimenti:", err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, sorting, debouncedColumnFilters, searchTerm, refreshList]);

    useEffect(() => {
        fetchMovimenti();
        const fetchAziendaData = async () => {
            try {
                const response = await getDatiAzienda();
                setDatiAzienda(response.data);
            } catch (err) {
                console.error("Errore nel caricamento dei dati azienda:", err);
            }
        };
        fetchAziendaData();
    }, [fetchMovimenti, refreshList]);

    useEffect(() => {
        if (isPrinting) {
            const handleAfterPrint = () => setIsPrinting(false);
            window.addEventListener('afterprint', handleAfterPrint);
            window.print();
            return () => window.removeEventListener('afterprint', handleAfterPrint);
        }
    }, [isPrinting]);

    // Reset selezione quando i dati cambiano
    useEffect(() => {
        setSelectedMovimenti(new Set());
        setSelectAll(false);
    }, [currentPage, movimenti]);

    // Aggiorna selectAll quando cambia la selezione
    useEffect(() => {
        if (movimenti.length > 0) {
            const allSelected = movimenti.every(movimento => selectedMovimenti.has(movimento.id));
            setSelectAll(allSelected);
        }
    }, [selectedMovimenti, movimenti]);
    
    const columns = useMemo(() => {
        const baseColumns = [
            // Colonna checkbox per selezione
            columnHelper.accessor('select', {
                id: 'select',
                header: ({ table }) => (
                    <Form.Check
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                ),
                cell: ({ row }) => (
                    <Form.Check
                        type="checkbox"
                        checked={selectedMovimenti.has(row.original.id)}
                        onChange={(e) => handleSelectMovimento(row.original.id, e.target.checked)}
                    />
                ),
                enableSorting: false,
                enableColumnFilter: false,
                enableResizing: false,
                enableHiding: false,
                size: 60,
            }),
            
            // Colonne dati esistenti
            ...Object.entries(allColumnsMap).map(([key, value]) =>
                columnHelper.accessor(key, {
                    header: value,
                    cell: info => {
                        if (key === 'data_movimento') {
                            return new Date(info.getValue()).toLocaleDateString();
                        }
                        if (key === 'titolo_prodotto') {
                            const titolo = info.getValue();
                            if (titolo == null) return 'N/A';
                            const { tipo_prodotto, prodotto_id } = info.row.original;
                            const path = getProdottoPath(tipo_prodotto, prodotto_id);
                            return path ? <Link to={path} state={BACK_TO_MOVIMENTI}>{titolo}</Link> : titolo;
                        }
                        if (key === 'riferimento_ordine_id') {
                            const ordineId = info.getValue();
                            return ordineId != null ? <Link to={`/ordini/${ordineId}`} state={BACK_TO_MOVIMENTI}>{ordineId}</Link> : 'N/A';
                        }
                        if (key === 'nome_cliente') {
                            const nomeCliente = info.getValue();
                            if (nomeCliente == null) return 'N/A';
                            const { cliente_id } = info.row.original;
                            return cliente_id ? <Link to={`/clienti/${cliente_id}`} state={BACK_TO_MOVIMENTI}>{nomeCliente}</Link> : nomeCliente;
                        }
                        return info.getValue() != null ? String(info.getValue()) : 'N/A';
                    },
                    // Cliente non è una colonna reale del movimento: nessun filtro/sort lato server
                    enableColumnFilter: key !== 'nome_cliente',
                    enableSorting: key !== 'nome_cliente',
                    meta: {
                      filterVariant: ['tipo_prodotto', 'tipo_movimento'].includes(key) ? 'select' : 'text',
                      filterOptions: distinctValues[key],
                    },
                })
            ),
        ];
        
        // Forza sempre select come prima colonna
        const selectColumn = baseColumns.find(col => col.id === 'select');
        const otherColumns = baseColumns.filter(col => col.id !== 'select');
        return [selectColumn, ...otherColumns];
    }, [distinctValues, selectedMovimenti, selectAll]);

    const onColumnVisibilityChange = (updater) => {
        const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
        setColumnVisibility(newState);
        localStorage.setItem(visibilityKey, JSON.stringify(newState));
    };

    const table = useReactTable({
        data: movimenti, 
        columns, 
        state: { columnVisibility, sorting, columnFilters, columnSizing, columnOrder },
        onColumnVisibilityChange,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnSizingChange: setColumnSizing,
        onColumnOrderChange: setColumnOrder,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        enableColumnReordering: true,
        manualFiltering: true,
    });
    
    const handleAutoResize = (column) => {
        const headerText = String(column.columnDef.header);
        const cells = table.getRowModel().rows.map(row => String(row.original[column.id] ?? ''));
        const charMultiplier = 8, padding = 30;
        const headerWidth = headerText.length * charMultiplier;
        const maxWidth = cells.reduce((max, cell) => Math.max(max, cell.length * charMultiplier), headerWidth);
        const newSize = Math.ceil(maxWidth) + padding;
        table.setColumnSizing(prev => ({ ...prev, [column.id]: newSize }));
    };



    // Funzioni per la selezione dei movimenti
    const handleSelectMovimento = (movimentoId, isSelected) => {
        setSelectedMovimenti(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(movimentoId);
            } else {
                newSet.delete(movimentoId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedMovimenti(new Set(movimenti.map(m => m.id)));
        } else {
            setSelectedMovimenti(new Set());
        }
        setSelectAll(isSelected);
    };

    const handleDeleteSelectedMovimenti = async () => {
        if (selectedMovimenti.size === 0) {
            toast.error("Seleziona almeno un movimento da eliminare.");
            return;
        }

        const count = selectedMovimenti.size;
        if (window.confirm(`Sei sicuro di voler eliminare ${count} movimento${count > 1 ? 'i' : ''} selezionat${count > 1 ? 'i' : 'o'}? Questa azione ripristinerà automaticamente i relativi stock.`)) {
            try {
                const movimentoIds = Array.from(selectedMovimenti);
                await deleteMovimentiMagazzino(movimentoIds);
                setMessage(`${count} movimento${count > 1 ? 'i' : ''} eliminat${count > 1 ? 'i' : 'o'} con successo.`);
                setSelectedMovimenti(new Set());
                setSelectAll(false);
                fetchMovimenti();
            } catch (err) {
                console.error('Errore eliminazione movimenti:', err);
                setError("Errore durante l'eliminazione dei movimenti di magazzino.");
            }
        }
    };

    const handleDeleteSingleMovimento = async (movimentoId) => {
        if (window.confirm("Sei sicuro di voler eliminare questo movimento? Questa azione ripristinerà automaticamente il relativo stock.")) {
            try {
                await deleteMovimentoMagazzino(movimentoId);
                setMessage("Movimento eliminato con successo.");
                fetchMovimenti();
            } catch (err) {
                console.error('Errore eliminazione movimento:', err);
                setError("Errore durante l'eliminazione del movimento di magazzino.");
            }
        }
    };

    const handleShowRectifyModal = () => setShowRectifyModal(true);
    const handleCloseRectifyModal = () => {
        setShowRectifyModal(false);
        setNewMovimentoData({ prodotto_id: '', tipo_prodotto: 'Film', tipo_movimento: 'Carico', quantita: '', descrizione: '' });
    };

    const handleRectifyFormChange = (e) => {
        const { name, value } = e.target;
        setNewMovimentoData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateRectifyMovimento = async (e) => {
        e.preventDefault();
        try {
            const movimentoToCreate = {
                ...newMovimentoData,
                prodotto_id: parseInt(newMovimentoData.prodotto_id),
                quantita: parseInt(newMovimentoData.quantita),
                data_movimento: new Date().toISOString().split('T')[0],
            };
            await createMovimentoMagazzino(movimentoToCreate);
            setMessage("Movimento di rettifica creato con successo.");
            handleCloseRectifyModal();
            setRefreshList(prev => prev + 1);
        } catch (err) {
            setError("Errore durante la creazione del movimento di rettifica.");
        }
    };

    const handlePrint = () => {
        if (datiAzienda) setIsPrinting(true);
        else setError("Dati aziendali non ancora caricati. Riprova tra un momento.");
    };

    const handleResetFilters = () => {
        table.resetColumnFilters();
        setSorting([]);
        setSearchTerm('');
        setCurrentPage(1);
    };

    const renderPaginationItems = () => {
        const totalPages = Math.ceil(totalMovimenti / itemsPerPage);
        const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
        if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
        else {
            if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
            else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
            else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
        }
        return pageNumbers;
    };

    const HeaderComponent = ({ header }) => {
        const { column } = header;
        const isSorted = column.getIsSorted();
        return (
          <th style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa', width: `${column.getSize()}px` }}>
            <div className="d-flex align-items-center justify-content-between p-2 h-100" style={{ width: '100%', overflow: 'hidden' }}>
              <span 
                className="fw-bold text-center" 
                style={{ whiteSpace: 'normal', flexGrow: 1, cursor: column.getCanSort() ? 'pointer' : 'default' }}
                onClick={column.getCanSort() ? column.getToggleSortingHandler() : undefined}
              >
                {flexRender(column.columnDef.header, header.getContext())}
                {isSorted && (
                  <FontAwesomeIcon 
                    icon={isSorted === 'asc' ? faSortUp : faSortDown} 
                    className="ms-1" 
                    size="sm"
                  />
                )}
              </span>
            </div>
            {header.column.getCanResize() && (
              <div 
                onMouseDown={header.getResizeHandler()} 
                onTouchStart={header.getResizeHandler()} 
                className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`} 
                style={{ 
                  transform: header.column.getIsResizing() ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)` : '' 
                }} 
              />
            )}
          </th>
        );
    };

    const hasManualFilters = table.getState().columnFilters.length > 0 || searchTerm;

    // Helper function per badge tipo movimento
    const getTipoMovimentoBadgeVariant = (tipo) => {
        const tipoLower = (tipo || '').toLowerCase();
        if (tipoLower.includes('carico')) return 'success';
        if (tipoLower.includes('scarico')) return 'danger';
        if (tipoLower.includes('rettifica')) return 'warning';
        return 'secondary';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Funzione per renderizzare la card mobile di un movimento
    const renderMovimentoCard = useCallback((movimento, isSelected, onToggleSelect) => {
        return (
            <Row>
                <Col xs={2} className="d-flex align-items-center justify-content-center">
                    <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                        style={{ transform: 'scale(1.3)' }}
                    />
                </Col>
                <Col xs={10}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h6 className="mb-1">
                                <FontAwesomeIcon icon={faBoxes} className="me-2 text-muted" />
                                {movimento.titolo_prodotto || 'N/A'}
                            </h6>
                            <small className="text-muted">
                                <Badge bg="secondary" className="me-2">{movimento.tipo_prodotto || 'N/A'}</Badge>
                                ID: {movimento.id}
                            </small>
                        </div>
                        <Badge bg={getTipoMovimentoBadgeVariant(movimento.tipo_movimento)}>
                            {movimento.tipo_movimento || 'N/A'}
                        </Badge>
                    </div>
                    <Row className="mb-2">
                        <Col xs={6}>
                            <small className="text-muted d-block">Quantità</small>
                            <strong style={{ fontSize: '1.1rem' }}>{movimento.quantita || 0}</strong>
                        </Col>
                        <Col xs={6}>
                            <small className="text-muted d-block">Data</small>
                            <strong>{formatDate(movimento.data_movimento)}</strong>
                        </Col>
                    </Row>
                    {movimento.descrizione && (
                        <div className="mb-2">
                            <small className="text-muted">Descrizione:</small>
                            <div style={{ fontSize: '0.9rem' }}>{movimento.descrizione}</div>
                        </div>
                    )}
                    {movimento.riferimento_ordine_id && (
                        <div>
                            <small className="text-muted">
                                Ordine: <Badge bg="info">#{movimento.riferimento_ordine_id}</Badge>
                                {movimento.nome_cliente && <span className="ms-2">{movimento.nome_cliente}</span>}
                            </small>
                        </div>
                    )}
                </Col>
            </Row>
        );
    }, []);

    // Contenuto dropdown azioni per mobile
    const actionsDropdownContent = useMemo(() => (
        <>
            <Dropdown.Item onClick={handleShowRectifyModal}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Registra Rettifica
            </Dropdown.Item>
            <Dropdown.Item onClick={handlePrint} disabled={!datiAzienda}>
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Stampa Lista
            </Dropdown.Item>
        </>
    ), [datiAzienda]);

    if (isPrinting) {
        return <MovimentiMagazzinoPrint movimenti={movimenti} datiAzienda={datiAzienda} />;
    }

    const statsCards = [
        { icon: faWarehouse, title: "Totale Movimenti", value: totalMovimenti, color: "primary" },
    ];

    return (
        <>
        <StandardPageHeader title="MOVIMENTI MAGAZZINO" statsCards={statsCards} />
        
        <Card>
            <Card.Header className="sticky-card-header">
                <Row className="align-items-center mb-3">
                    <Col md={6} className="d-flex justify-content-start align-items-center flex-wrap gap-2">
                        <Button variant="primary" onClick={handleShowRectifyModal} size="sm">
                            <FontAwesomeIcon icon={faPlus} className="me-1" />REGISTRA RETTIFICA
                        </Button>
                        <Button variant="info" onClick={handlePrint} size="sm">
                            <FontAwesomeIcon icon={faPrint} className="me-1" />STAMPA
                        </Button>
                        {selectedMovimenti.size === 1 && (
                            <Button 
                                variant="outline-danger" 
                                onClick={() => {
                                    const selectedId = Array.from(selectedMovimenti)[0];
                                    handleDeleteSingleMovimento(selectedId);
                                }} 
                                size="sm"
                                title="Elimina movimento selezionato"
                            >
                                <FontAwesomeIcon icon={faTrash} className="me-1" />ELIMINA SELEZIONATO
                            </Button>
                        )}
                        {selectedMovimenti.size > 1 && (
                            <Button 
                                variant="warning" 
                                onClick={handleDeleteSelectedMovimenti} 
                                size="sm"
                                title={`Elimina ${selectedMovimenti.size} movimenti selezionati`}
                            >
                                <FontAwesomeIcon icon={faTrash} className="me-1" />ELIMINA SELEZIONATI ({selectedMovimenti.size})
                            </Button>
                        )}

                    </Col>
                    <Col md={6} className="d-flex justify-content-end align-items-center gap-2">
                        {/* Ricerca Globale */}
                        <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                            <InputGroup.Text>
                                <FontAwesomeIcon icon={faSearch} />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Cerca in tutti i campi..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {searchTerm && (
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={() => setSearchTerm('')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            )}
                        </InputGroup>
                        
                        {/* Dropdown Configurazione */}
                        <Dropdown autoClose="outside">
                            <Dropdown.Toggle variant="outline-primary" id="dropdown-table-config" size="sm">
                                <FontAwesomeIcon icon={faCog} className="me-1" />
                                Configura
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '400px', maxHeight: '500px', overflowY: 'auto' }}>
                                {/* Sezione Ordinamento */}
                                <div className="p-3 border-bottom">
                                    <h6 className="fw-bold mb-3"><FontAwesomeIcon icon={faSort} className="me-2" />Ordinamento</h6>
                                    <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
                                </div>
                                
                                {/* Sezione Ordine Colonne (drag & drop) */}
                                <div className="p-3 border-bottom">
                                    <ColumnOrderController table={table} allColumnsMap={allColumnsMap} />
                                </div>

                                {/* Sezione Visibilità Colonne */}
                                <div className="p-3 border-bottom">
                                    <h6 className="fw-bold mb-3"><FontAwesomeIcon icon={faColumns} className="me-2" />Visibilità Colonne</h6>
                                    <div className="d-flex flex-column">
                                        {table.getAllColumns().filter(c => c.id !== 'select').map(c => (
                                            <Form.Check 
                                                key={c.id} 
                                                type="checkbox" 
                                                label={allColumnsMap[c.id]} 
                                                checked={c.getIsVisible()} 
                                                onChange={c.getToggleVisibilityHandler()} 
                                                className="mb-2" 
                                            />
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Sezione Vista */}
                                <div className="p-3">
                                    <h6 className="fw-bold mb-3">Vista</h6>
                                    <Form.Check 
                                        type="switch" 
                                        id="condensed-switch"
                                        label="Vista compatta" 
                                        checked={isCondensed} 
                                        onChange={(e) => setIsCondensed(e.target.checked)}
                                        className="mb-2" 
                                    />
                                    
                                    {/* Pulsante Reset */}
                                    <hr />
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="w-100" 
                                        onClick={handleResetFilters} 
                                        disabled={!hasManualFilters && sorting.length === 0}
                                    >
                                        <FontAwesomeIcon icon={faUndo} className="me-1" />
                                        Azzera Filtri e Ordinamento
                                    </Button>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {loading && <div className="text-center"><Spinner animation="border" /></div>}
                {message && <Alert variant="success" onClose={() => setMessage(null)} dismissible>{message}</Alert>}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {!loading && !error && (
                    <>
                        {isMobile ? (
                            <MobileListLayout
                                items={movimenti}
                                renderCard={renderMovimentoCard}
                                entityName="movimento"
                                createPath="/movimenti/nuova-rettifica"
                                selectedIds={Array.from(selectedMovimenti)}
                                onToggleSelect={(id) => handleSelectMovimento(id, !selectedMovimenti.has(id))}
                                onDeleteMultiple={handleDeleteSelectedMovimenti}
                                actionsDropdownContent={actionsDropdownContent}
                                showCreateButton={false}
                            />
                        ) : (
                            <div style={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
                                <Table 
                                    striped 
                                    bordered 
                                    hover 
                                    size={isCondensed ? 'sm' : undefined}
                                    style={{ width: table.getTotalSize() }} 
                                    className="aligned-table"
                                >
                                    <thead>
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <React.Fragment key={headerGroup.id}>
                                            <tr>
                                                {headerGroup.headers.map(header => <HeaderComponent key={header.id} header={header} />)}
                                            </tr>
                                            <tr key={`${headerGroup.id}-filters`}>
                                              {headerGroup.headers.map(header => (
                                                <th key={`${header.id}-filter`} style={{ backgroundColor: '#f8f9fa' }}>
                                                  {header.column.getCanFilter() ? (
                                                    <AdvancedFilter
                                                      column={header.column}
                                                      dateColumns={['data_movimento']}
                                                      numericColumns={['id', 'prodotto_id', 'quantita', 'quantita_prima', 'quantita_dopo', 'riferimento_ordine_id', 'riferimento_dettaglio_ordine_id']}
                                                    />
                                                  ) : null}
                                                </th>
                                              ))}
                                            </tr>
                                          </React.Fragment>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map(row => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center">
                <Form.Group as={Row} className="align-items-center mb-0">
                    <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
                    <Col xs="auto">
                        <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                        </Form.Select>
                    </Col>
                </Form.Group>
                <Pagination size="sm" className="mb-0">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalMovimenti / itemsPerPage)} />
                    <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalMovimenti / itemsPerPage))} disabled={currentPage === Math.ceil(totalMovimenti / itemsPerPage)} />
                </Pagination>
            </Card.Footer>
        </Card>

        <Modal show={showRectifyModal} onHide={handleCloseRectifyModal}>
            <Modal.Header closeButton>
                <Modal.Title>Registra Movimento di Rettifica</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleCreateRectifyMovimento}>
                    <Form.Group className="mb-3">
                        <Form.Label>ID Prodotto</Form.Label>
                        <Form.Control type="number" name="prodotto_id" value={newMovimentoData.prodotto_id} onChange={handleRectifyFormChange} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo Prodotto</Form.Label>
                        <Form.Control as="select" name="tipo_prodotto" value={newMovimentoData.tipo_prodotto} onChange={handleRectifyFormChange} required>
                            <option value="Film">Film</option>
                            <option value="Musica">Musica</option>
                            <option value="Servizio">Servizio</option>
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo Movimento (Carico/Scarico per rettifica)</Form.Label>
                        <Form.Control as="select" name="tipo_movimento" value={newMovimentoData.tipo_movimento} onChange={handleRectifyFormChange} required>
                            <option value="Carico">Carico</option>
                            <option value="Scarico">Scarico</option>
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Quantità</Form.Label>
                        <Form.Control type="number" name="quantita" value={newMovimentoData.quantita} onChange={handleRectifyFormChange} required min="1" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Descrizione</Form.Label>
                        <Form.Control as="textarea" name="descrizione" value={newMovimentoData.descrizione} onChange={handleRectifyFormChange} rows={3} />
                    </Form.Group>
                    <Button variant="primary" type="submit">Registra Rettifica</Button>
                </Form>
            </Modal.Body>
        </Modal>
        </>
    );
};

export default MovimentiMagazzinoList;
